// src/components/PatientDetails/panels/TableSidebar.tsx
// -----------------------------------------------------------------------------
// TableSidebar — left rail navigation between patient data tables (Athena theme)
// -----------------------------------------------------------------------------

import {
  Add,
  Biotech,
  Bloodtype,
  Coronavirus,
  Dashboard,
  FamilyRestroom,
  FiberManualRecord,
  Fingerprint,
  KeyboardArrowRight,
  Medication,
  MedicalServices,
  MonitorHeart,
  OpenInNew,
  Person,
  Scanner,
  Science,
  WarningAmber,
  Search as SearchIcon,
  Straighten,
  TableChart,
  Timeline,
  Vaccines,
  WaterDrop,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import DnaIcon from "./DnaIcon";
import React, { useCallback, useMemo, useState } from "react";
import { useFieldGroups } from "../../../hooks/useFieldGroups";
import { useFieldSchema } from "../../../hooks/useFieldSchema";
import { useTranslation } from "../../../i18n";
import { athenaColors } from "../../../theme/palette";

/** Single source of truth for sidebar width — also imported by PatientDetailsLayout */
export const SIDEBAR_WIDTH = 260;


// -----------------------------------------------------------------------------

const NARROW_SCROLLBAR_SX = {
  "&::-webkit-scrollbar": { width: 3, height: 3 },
  "&::-webkit-scrollbar-track": {
    background: alpha(athenaColors.periwinkle, 0.06),
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(athenaColors.periwinkle, 0.25),
    borderRadius: 2,
  },
};

type TableRow = Record<string, unknown>;

export interface TableSidebarProps {

  /** Retirement flag (066 D / 4.0): when false, hide legacy Baseline/History sections. */
  legacyTablesEnabled?: boolean;
  /** Universal tables have data */
  hasPatientProfile: boolean;
  hasMolecularProfile: boolean;
  hasCancerProfile: boolean;
  hasHematologicalProfile: boolean;
  hasTrialEnrollment: boolean;
  hasAdverseEvents: boolean;
  hasMedications: boolean;
  hasDiagnoses: boolean;
  hasProcedures: boolean;
  hasTumorAssessments: boolean;
  hasDiseaseStatuses: boolean;
  hasImaging: boolean;
  hasSystemicTherapies: boolean;
  hasLabResults: boolean;
  hasFamilyHistory: boolean;
  /** Raw universal data — used to compute per-category filled state */
  universalData?: {
    patientProfile: TableRow[];
    molecularProfile: TableRow[];
    cancerProfile: TableRow[];
    cancerObservation: TableRow[];
    hematologicalProfile: TableRow[];
    trialEnrollment: TableRow[];
    adverseEvents: TableRow[];
    medications: TableRow[];
    diagnoses: TableRow[];
    procedures: TableRow[];
    tumorAssessments: TableRow[];
    diseaseStatuses: TableRow[];
    imaging: TableRow[];
    systemicTherapies: TableRow[];
    labResults: TableRow[];
    familyHistory: TableRow[];
  };
  /** Raw domain data — used to compute per-category filled state for statics */
  allDomainData?: Record<string, { static: TableRow[]; event: TableRow[] }>;
  /** Domain-specific tables */
  domainSections: Array<{
    domainKey: string;
    label: string;
    hasStatic: boolean;
    hasEvent: boolean;
    events?: Array<{ event_id: number | string; event_type?: string; event_date?: string }>;
  }>;
  /** Currently active section ID */
  activeSection: string;
  /** Handler when user clicks a section */
  onSectionChange: (sectionId: string) => void;
  /** Action panel buttons - pass through */
  actionButtons?: React.ReactNode;
  /** Callback to create a universal table record (tableType e.g. "patientprofiletable") */
  onCreateUniversalTable?: (tableType: string) => Promise<void>;
  /** Available domain types that can be added */
  availableDomainTypes?: Array<{ value: string; label: string }>;
  /** Callback to add a new domain */
  onAddDomain?: (domainKey: string) => void;
  /** Callback when user clicks "+ Add Event" in a domain's Patient Journey sub-nav.
   *  Second arg is the anchor element for positioning a popover near the click. */
  onAddEvent?: (domainKey: string, anchorEl?: HTMLElement) => void;
  /** Callback when user clicks a category sub-item (sectionId, categoryName) */
  onSubItemClick?: (sectionId: string, categoryName: string) => void;
  /** Callback when user clicks the "+" button on a category sub-item — opens category modal */
  onCategoryAction?: (sectionId: string, categoryName: string) => void;
  /** Callback when user clicks "+ Add" on a universal event-style section
   *  (Medications / Adverse Events) — creates a NEW record via the creation modal. */
  onCreateRecord?: (sectionId: string) => void;
}

type NavItemConfig = {
  sectionId: string;
  labelKey: string;
  tableType: string;
  Icon: React.ComponentType<SvgIconProps>;
  hasData: boolean;
};

// -----------------------------------------------------------------------------

function NavRow({
  sectionId,
  label,
  Icon,
  active,
  hasData,
  onActivate,
  onCreateTable,
  creating,
  indented,
  dense,
  addButton,
}: {
  sectionId: string;
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  active: boolean;
  hasData: boolean;
  onActivate: (id: string) => void;
  onCreateTable?: () => void;
  creating?: boolean;
  indented?: boolean;
  /** Tighter row (shorter height + padding) for deeper nav levels, e.g. a
   *  domain's Baseline/History sub-items which otherwise read as too airy. */
  dense?: boolean;
  addButton?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const handleClick = useCallback(() => {
    onActivate(sectionId);
  }, [onActivate, sectionId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onActivate(sectionId);
      }
    },
    [onActivate, sectionId]
  );

  const handleCreate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCreateTable?.();
    },
    [onCreateTable]
  );

  const emptyOpacity = 0.35;
  const normalOpacity = 0.7;
  const baseOpacity = hasData ? normalOpacity : emptyOpacity;

  return (
    <Tooltip
      title={hasData ? label : `${label} — ${t("table_sidebar.no_data", "no data yet")}`}
      placement="right"
      enterDelay={400}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          display: "flex",
          alignItems: "center",
          height: dense ? 28 : 36,
          pl: indented ? 3.5 : 2,
          pr: 2,
          py: dense ? 0.25 : 0.5,
          gap: 1.5,
          cursor: "pointer",
          borderLeft: "3px solid",
          borderLeftColor: active ? athenaColors.periwinkle : "transparent",
          backgroundColor: active
            ? alpha(athenaColors.periwinkle, 0.15)
            : "transparent",
          transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
          ...(!active && {
            "&:hover": {
              backgroundColor: alpha(athenaColors.white, 0.05),
              "& .TableSidebar-navLabel": {
                color: alpha(athenaColors.white, Math.min(baseOpacity + 0.15, 0.85)),
              },
              "& .TableSidebar-navIcon": {
                color: alpha(athenaColors.white, Math.min(baseOpacity + 0.15, 0.85)),
              },
              "& .TableSidebar-createBtn": {
                opacity: 1,
              },
            },
          }),
        }}
      >
        <Icon
          className="TableSidebar-navIcon"
          sx={{
            fontSize: 14,
            color: active
              ? athenaColors.periwinkle
              : alpha(athenaColors.white, baseOpacity),
            transition: "color 0.15s ease",
          }}
        />
        <Typography
          className="TableSidebar-navLabel"
          component="span"
          sx={{
            fontSize: "0.8rem",
            fontWeight: active ? 600 : 500,
            color: active
              ? athenaColors.white
              : alpha(athenaColors.white, baseOpacity),
            transition: "color 0.15s ease, font-weight 0.15s ease",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {label}
        </Typography>
        {addButton}
        {!hasData && onCreateTable && (
          <Tooltip title={t("table_sidebar.create_table", "Create")} placement="right">
            <IconButton
              className="TableSidebar-createBtn"
              size="small"
              onClick={handleCreate}
              disabled={creating}
              sx={{
                p: 0,
                width: 16,
                height: 16,
                flexShrink: 0,
                opacity: 0.5,
                transition: "opacity 0.15s ease",
                color: athenaColors.periwinkle,
                "&:hover": {
                  color: athenaColors.white,
                  backgroundColor: alpha(athenaColors.periwinkle, 0.2),
                },
              }}
            >
              {creating ? (
                <CircularProgress size={10} sx={{ color: "inherit" }} />
              ) : (
                <Add sx={{ fontSize: 13 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Tooltip>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        px: 2,
        py: 0.5,
        fontSize: "0.65rem",
        fontWeight: 600,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: alpha(athenaColors.white, 0.35),
      }}
    >
      {children}
    </Typography>
  );
}

function SubItem({
  label,
  active,
  hasData,
  onClick,
  onAdd,
  depth = 0,
  expandable,
  expanded,
}: {
  label: string;
  active?: boolean;
  hasData?: boolean;
  onClick: () => void;
  onAdd?: () => void;
  /** 0 = record / category level (pl 5); 1 = a record's nested field-group (pl 6.5). */
  depth?: 0 | 1;
  /** Show a disclosure chevron (used for record nodes that expand to field-groups). */
  expandable?: boolean;
  expanded?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => { onClick(); if (!hasData) onAdd?.(); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); if (!hasData) onAdd?.(); } }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: depth === 1 ? 6.75 : 5,
        pr: 2,
        py: 0.25,
        height: 26,
        cursor: "pointer",
        borderLeft: "3px solid transparent",
        transition: "background-color 0.12s ease",
        "&:hover": {
          backgroundColor: alpha(athenaColors.white, 0.05),
          "& .SubItem-label": { color: alpha(athenaColors.white, 0.7) },
          "& .SubItem-addBtn": { opacity: 1 },
        },
        ...(active && {
          borderLeftColor: alpha(athenaColors.periwinkle, 0.5),
          backgroundColor: alpha(athenaColors.periwinkle, 0.08),
        }),
      }}
    >
      {expandable && (
        <KeyboardArrowRight
          sx={{
            fontSize: 13,
            flexShrink: 0,
            ml: -0.75,
            color: alpha(athenaColors.white, 0.5),
            transform: expanded ? "rotate(90deg)" : "none",
            transition: "transform 0.15s ease",
          }}
        />
      )}
      <Typography
        className="SubItem-label"
        sx={{
          fontSize: "0.7rem",
          fontWeight: hasData ? 500 : 400,
          color: hasData
            ? (active ? alpha(athenaColors.white, 0.85) : alpha(athenaColors.white, 0.6))
            : (active ? alpha(athenaColors.white, 0.55) : alpha(athenaColors.white, 0.35)),
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: 1.2,
          flex: 1,
        }}
      >
        {label}
      </Typography>
      {onAdd && (
        <Tooltip
          title={hasData
            ? t("table_sidebar.show_fields", "Show related fields")
            : t("table_sidebar.add_fields", "Add fields")
          }
          placement="right"
        >
          <IconButton
            className="SubItem-addBtn"
            size="small"
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            sx={{
              p: 0,
              width: 14,
              height: 14,
              flexShrink: 0,
              opacity: hasData ? 0.25 : 0.4,
              transition: "opacity 0.15s ease",
              color: athenaColors.periwinkle,
              "&:hover": {
                color: athenaColors.white,
                backgroundColor: alpha(athenaColors.periwinkle, 0.2),
              },
            }}
          >
            {hasData ? (
              <OpenInNew sx={{ fontSize: 9 }} />
            ) : (
              <Add sx={{ fontSize: 11 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const SYSTEM_KEYS = new Set([
  "id", "event_id", "created_at", "modified_at", "created_by",
  "batch_id", "event_no", "text", "table", "processing_task_id",
  "processing_status", "patient", "environments", "platform_user",
  "is_validated", "progress", "eventtable_ptr", "found_with_parent",
  "institution_id", "dynamic_entities", "starter",
  "source", "is_locked", "last_ai_extraction", "source_documents",
  "locked_fields",
  "human_edited_fields",
  "migration_notes",
  // oncology-course anchor FK on the residue tables — relational, not clinical.
  "cancer", "cancer_id",
  // Imaging image-display wiring (P1): the source-image link + resolved URL are
  // display-only, never clinical/editable fields.
  "image_document", "image_url",
]);

function computeFilledCategories(
  categories: Record<string, string[]> | undefined,
  rows: TableRow[] | undefined,
): Set<string> {
  const filled = new Set<string>();
  if (!categories || !rows || rows.length === 0) return filled;
  for (const [cat, fields] of Object.entries(categories)) {
    const hasCatData = rows.some((row) =>
      fields.some((f) => !SYSTEM_KEYS.has(f) && row[f] != null && row[f] !== "")
    );
    if (hasCatData) filled.add(cat);
  }
  return filled;
}

// Drop field-group categories that are entirely ANOTHER cancer's residue. For a
// per-cancer field-scoped table (CancerProfile, Procedures, Diagnosis, …) the
// field-schema endpoint returns `hidden_scoped_fields` — the foreign-cancer
// residue columns hidden for this patient's cancer (the same set dropped from
// the editable `fields`). A category is kept if it has >= 1 field NOT in that
// hidden set; base groups whose fields are locked/derived but still in-scope
// (e.g. "Cancer" → cancer_type) are kept because those fields are not hidden by
// cancer scoping. Non-scoped tables send an empty/absent list → every group is
// kept (no-op). While the scoped schema is still loading (hidden === undefined)
// all groups are returned so the nav never flashes empty.
function scopedCategoryNames(
  fieldCategories: Record<string, string[]> | undefined,
  hiddenScopedFields: string[] | undefined,
): string[] {
  if (!fieldCategories) return [];
  const names = Object.keys(fieldCategories);
  if (!hiddenScopedFields || hiddenScopedFields.length === 0) return names;
  const hidden = new Set(hiddenScopedFields);
  return names.filter((name) =>
    (fieldCategories[name] ?? []).some((f) => !hidden.has(f))
  );
}

const normalizeEventType = (s?: string) =>
  s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Event";

// "+ Add" / "+ Event" icon button rendered inside a section NavRow. Shared by the
// domain History section and the universal event-style sections (Medications /
// Adverse Events) so both surface the same add affordance.
function SidebarAddButton({ title, onClick }: { title: string; onClick: (e: React.MouseEvent<HTMLElement>) => void }) {
  return (
    <Tooltip title={title} placement="right">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          p: 0,
          width: 16,
          height: 16,
          flexShrink: 0,
          opacity: 0.5,
          transition: "opacity 0.15s ease",
          color: athenaColors.periwinkle,
          "&:hover": {
            opacity: 1,
            color: athenaColors.white,
            backgroundColor: alpha(athenaColors.periwinkle, 0.2),
          },
        }}
      >
        <Add sx={{ fontSize: 13 }} />
      </IconButton>
    </Tooltip>
  );
}

// Two-level record tree shared by every event-style table: each RECORD is a node
// (collapsed by default); expanding it reveals its FIELD GROUPS (the category
// headers, NOT field values). Clicking a record navigates the main content to it;
// clicking a group deep-links to that group. Reuses the SubItem row used by the
// domain History event sub-items so the universal event tables (Medications /
// Adverse Events) and the per-cancer History tables render identically. When a
// table has no named field-categories (e.g. per-cancer event tables, which group
// by event_type only), records have no children and a click just focuses the row,
// preserving the existing History behavior.
function EventRecordNodes({
  sectionId,
  records,
  groupNames,
  onSubItemClick,
}: {
  sectionId: string;
  records: Array<{ id: string | number; label: string }>;
  groupNames: string[];
  onSubItemClick?: (sectionId: string, categoryName: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const hasGroups = groupNames.length > 0;
  return (
    <>
      {records.map((rec) => {
        const key = String(rec.id);
        const isOpen = expanded.has(key);
        return (
          <React.Fragment key={key}>
            <SubItem
              label={rec.label}
              hasData
              expandable={hasGroups}
              expanded={isOpen}
              onClick={() => {
                // Navigate the main content to this record (expand its row).
                onSubItemClick?.(sectionId, `event-${rec.id}`);
                // Toggle its field-groups in the sidebar (collapsed by default).
                if (hasGroups) {
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }
              }}
            />
            {hasGroups && (
              <Collapse in={isOpen} timeout={160} unmountOnExit>
                {groupNames.map((g) => (
                  <SubItem
                    key={g}
                    label={g}
                    hasData
                    depth={1}
                    onClick={() => onSubItemClick?.(sectionId, `eventfield-${rec.id}::${g}`)}
                  />
                ))}
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

// Sidebar nav for the universal EVENT-style tables (Medications, Adverse Events).
// Unlike the single-record profile tables (which list field CATEGORIES with a
// per-category "+"), these hold many rows per patient, so they mirror the domain
// History pattern: a "+ Add" on the section header + each record as a node whose
// field-groups nest underneath. See EventRecordNodes.
function UniversalEventRecordsNav({
  sectionId,
  label,
  Icon,
  active,
  hasData,
  onActivate,
  onSubItemClick,
  onCreateRecord,
  records,
}: {
  sectionId: string;
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  active: boolean;
  hasData: boolean;
  onActivate: (id: string) => void;
  onSubItemClick?: (sectionId: string, categoryName: string) => void;
  onCreateRecord?: (sectionId: string) => void;
  records: Array<{ id: string | number; label: string }>;
}) {
  const { t } = useTranslation();
  const cfg = UNIVERSAL_FIELD_GROUP_CONFIG[sectionId];
  const { fieldGroups } = useFieldGroups(cfg?.domainKey ?? "", cfg?.tableType ?? "");
  const groupNames = useMemo(
    () => Object.keys(fieldGroups?.field_categories ?? {}),
    [fieldGroups]
  );

  return (
    <>
      <NavRow
        sectionId={sectionId}
        label={label}
        Icon={Icon}
        active={active}
        hasData={hasData}
        onActivate={onActivate}
        addButton={
          onCreateRecord ? (
            <SidebarAddButton
              title={t("table_sidebar.add_record", "Add record")}
              onClick={(e) => {
                e.stopPropagation();
                onCreateRecord(sectionId);
              }}
            />
          ) : undefined
        }
      />
      <Collapse in={active && records.length > 0} timeout={200} unmountOnExit>
        <EventRecordNodes
          sectionId={sectionId}
          records={records}
          groupNames={groupNames}
          onSubItemClick={onSubItemClick}
        />
      </Collapse>
    </>
  );
}

// Joan, 2026-06-04 (cursor_logs/036 §17): the last three rows used to use
// bare keys ("hematological", "trial_enrollment", "adverse_events") which
// don't exist as top-level entries in the backend's `model_mapping` — the
// universal tables are *inlined* into every `cancer.X` entry instead (see
// backend/clinical/utils/model_mapping.py:242-249 + table_registry.py's
// UNIVERSAL_TABLE_KINDS). Aligning all five rows to `cancer.skin` matches
// the convention used by the first two rows; every cancer entry holds the
// same class object for each universal table so `cancer.skin` is a safe
// canonical lookup. Removes the three /api/field-groups/ 404s on patient
// details with zero backend touch.
const UNIVERSAL_FIELD_GROUP_CONFIG: Record<string, { domainKey: string; tableType: string }> = {
  patient_profile: { domainKey: "cancer.skin", tableType: "patientprofiletable" },
  molecular_profile: { domainKey: "cancer.skin", tableType: "molecularprofiletable" },
  cancer_profile: { domainKey: "cancer.skin", tableType: "cancerprofiletable" },
  hematological_profile: { domainKey: "cancer.skin", tableType: "hematologicalprofiletable" },
  trial_enrollment: { domainKey: "cancer.skin", tableType: "trialenrollmenttable" },
  adverse_events: { domainKey: "cancer.skin", tableType: "adverseeventtable" },
  medications: { domainKey: "cancer.skin", tableType: "medicationstatementtable" },
  diagnoses: { domainKey: "cancer.skin", tableType: "diagnosistable" },
  procedures: { domainKey: "cancer.skin", tableType: "procedurestable" },
  tumorassessments: { domainKey: "cancer.skin", tableType: "tumorassessmenttable" },
  diseasestatuses: { domainKey: "cancer.skin", tableType: "diseasestatustable" },
  imaging: { domainKey: "cancer.skin", tableType: "imagingtable" },
  systemictherapies: { domainKey: "cancer.skin", tableType: "systemictherapytable" },
  labresults: { domainKey: "cancer.skin", tableType: "labresulttable" },
  family_history: { domainKey: "cancer.skin", tableType: "familyhistorytable" },
};

function UniversalExpandableNav({
  sectionId,
  label,
  Icon,
  active,
  hasData,
  onActivate,
  onCreateTable,
  creating,
  onSubItemClick,
  onCategoryAction,
  tableData,
}: {
  sectionId: string;
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  active: boolean;
  hasData: boolean;
  onActivate: (id: string) => void;
  onCreateTable?: () => void;
  creating?: boolean;
  onSubItemClick?: (sectionId: string, categoryName: string) => void;
  onCategoryAction?: (sectionId: string, categoryName: string) => void;
  tableData?: TableRow[];
}) {
  const cfg = UNIVERSAL_FIELD_GROUP_CONFIG[sectionId];
  const { fieldGroups } = useFieldGroups(cfg?.domainKey ?? "", cfg?.tableType ?? "");
  const categories = useMemo(() => {
    const cats = fieldGroups?.field_categories;
    if (!cats) return [];
    return Object.keys(cats);
  }, [fieldGroups]);

  const filledCats = useMemo(
    () => computeFilledCategories(fieldGroups?.field_categories, tableData),
    [fieldGroups, tableData]
  );

  return (
    <>
      <NavRow
        sectionId={sectionId}
        label={label}
        Icon={Icon}
        active={active}
        hasData={hasData}
        onActivate={onActivate}
        onCreateTable={onCreateTable}
        creating={creating}
      />
      <Collapse in={active && categories.length > 0} timeout={200} unmountOnExit>
        {categories.map((cat) => (
          <SubItem
            key={cat}
            label={cat}
            hasData={filledCats.has(cat)}
            onClick={() => onSubItemClick?.(sectionId, cat)}
            onAdd={() => onCategoryAction?.(sectionId, cat)}
          />
        ))}
      </Collapse>
    </>
  );
}

function DomainExpandableNav({
  domain,
  legacyTablesEnabled = true,
  activeSection,
  onSectionChange,
  onAddEvent,
  onSubItemClick,
  onCategoryAction,
  staticData,
  isPrimary,
  hasCancerProfile,
  cancerProfileData,
  onCreateCancerProfile,
  creatingCancerProfile,
  hasDiagnoses,
  hasProcedures,
  hasTumorAssessments,
  hasDiseaseStatuses,
  hasImaging,
  hasSystemicTherapies,
  hasLabResults,
  diagnosisRecords,
  procedureRecords,
  tumorAssessmentRecords,
  diseaseStatusRecords,
  imagingRecords,
  systemicTherapyRecords,
  labResultRecords,
  onCreateRecord,
}: {
  domain: {
    domainKey: string;
    label: string;
    hasStatic: boolean;
    hasEvent: boolean;
    events?: Array<{ event_id: number | string; event_type?: string; event_date?: string }>;
  };
  /** Retirement flag (066 D / 4.0): when false, hide the legacy Baseline/History sections. */
  legacyTablesEnabled?: boolean;
  activeSection: string;
  onSectionChange: (sectionId: string) => void;
  onAddEvent?: (domainKey: string, anchorEl?: HTMLElement) => void;
  onSubItemClick?: (sectionId: string, categoryName: string) => void;
  onCategoryAction?: (sectionId: string, categoryName: string) => void;
  staticData?: TableRow[];
  /** C6: when true, the universal Diagnoses/Procedures nav rows render nested
   *  under this (the primary/first) cancer domain, next to Baseline/History. */
  isPrimary?: boolean;
  /** C6b: universal Cancer Profile (cancerprofiletable) rendered nested under the
   *  primary cancer domain, right after Baseline. Single-record profile table, so
   *  it uses the same category nav + create-when-empty affordance as Baseline. */
  hasCancerProfile?: boolean;
  cancerProfileData?: TableRow[];
  onCreateCancerProfile?: () => void;
  creatingCancerProfile?: boolean;
  hasDiagnoses?: boolean;
  hasProcedures?: boolean;
  hasTumorAssessments?: boolean;
  hasDiseaseStatuses?: boolean;
  hasImaging?: boolean;
  hasSystemicTherapies?: boolean;
  hasLabResults?: boolean;
  diagnosisRecords?: Array<{ id: string | number; label: string }>;
  procedureRecords?: Array<{ id: string | number; label: string }>;
  tumorAssessmentRecords?: Array<{ id: string | number; label: string }>;
  diseaseStatusRecords?: Array<{ id: string | number; label: string }>;
  imagingRecords?: Array<{ id: string | number; label: string }>;
  systemicTherapyRecords?: Array<{ id: string | number; label: string }>;
  labResultRecords?: Array<{ id: string | number; label: string }>;
  onCreateRecord?: (sectionId: string) => void;
}) {
  const { t } = useTranslation();
  const staticActive = activeSection === `${domain.domainKey}_static`;
  const eventActive = activeSection === `${domain.domainKey}_event`;

  // C6: field-group names for the nested universal Diagnoses/Procedures record
  // trees (mirrors UniversalEventRecordsNav). Canonical lookup via cancer.skin —
  // every cancer entry shares the same universal table class.
  // Scoped tables (Diagnosis/Procedures/Staging/Disease Status/Imaging/Systemic
  // Therapy) carry per-cancer residue GROUPS in FIELD_CATEGORIES. The category
  // LIST is cancer-independent (fetched via the canonical cancer.skin lookup),
  // but which groups are IN-SCOPE depends on the patient's cancer, so the
  // hidden-set comes from the field-schema fetched with THIS cancer's domain
  // (domain.domainKey). scopedCategoryNames() then drops other cancers' residue
  // groups. (Lab Results is not a scoped table — no residue groups — so it keeps
  // the plain category list.)
  const { fieldGroups: diagnosisFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.diagnoses.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.diagnoses.tableType
  );
  const { schema: diagnosisScopedSchema } = useFieldSchema(
    domain.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.diagnoses.tableType
  );
  const diagnosisGroupNames = useMemo(
    () =>
      scopedCategoryNames(
        diagnosisFieldGroups?.field_categories,
        diagnosisScopedSchema?.hidden_scoped_fields
      ),
    [diagnosisFieldGroups, diagnosisScopedSchema]
  );
  const { fieldGroups: procedureFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.procedures.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.procedures.tableType
  );
  const { schema: procedureScopedSchema } = useFieldSchema(
    domain.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.procedures.tableType
  );
  const procedureGroupNames = useMemo(
    () =>
      scopedCategoryNames(
        procedureFieldGroups?.field_categories,
        procedureScopedSchema?.hidden_scoped_fields
      ),
    [procedureFieldGroups, procedureScopedSchema]
  );
  const { fieldGroups: tumorAssessmentFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.tumorassessments.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.tumorassessments.tableType
  );
  const { schema: tumorAssessmentScopedSchema } = useFieldSchema(
    domain.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.tumorassessments.tableType
  );
  const tumorAssessmentGroupNames = useMemo(
    () =>
      scopedCategoryNames(
        tumorAssessmentFieldGroups?.field_categories,
        tumorAssessmentScopedSchema?.hidden_scoped_fields
      ),
    [tumorAssessmentFieldGroups, tumorAssessmentScopedSchema]
  );
  const { fieldGroups: diseaseStatusFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.diseasestatuses.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.diseasestatuses.tableType
  );
  const { schema: diseaseStatusScopedSchema } = useFieldSchema(
    domain.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.diseasestatuses.tableType
  );
  const diseaseStatusGroupNames = useMemo(
    () =>
      scopedCategoryNames(
        diseaseStatusFieldGroups?.field_categories,
        diseaseStatusScopedSchema?.hidden_scoped_fields
      ),
    [diseaseStatusFieldGroups, diseaseStatusScopedSchema]
  );
  const { fieldGroups: imagingFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.imaging.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.imaging.tableType
  );
  const { schema: imagingScopedSchema } = useFieldSchema(
    domain.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.imaging.tableType
  );
  const imagingGroupNames = useMemo(
    () =>
      scopedCategoryNames(
        imagingFieldGroups?.field_categories,
        imagingScopedSchema?.hidden_scoped_fields
      ),
    [imagingFieldGroups, imagingScopedSchema]
  );
  const { fieldGroups: systemicTherapyFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.systemictherapies.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.systemictherapies.tableType
  );
  const { schema: systemicTherapyScopedSchema } = useFieldSchema(
    domain.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.systemictherapies.tableType
  );
  const systemicTherapyGroupNames = useMemo(
    () =>
      scopedCategoryNames(
        systemicTherapyFieldGroups?.field_categories,
        systemicTherapyScopedSchema?.hidden_scoped_fields
      ),
    [systemicTherapyFieldGroups, systemicTherapyScopedSchema]
  );
  const { fieldGroups: labResultFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.labresults.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.labresults.tableType
  );
  const labResultGroupNames = useMemo(
    () => Object.keys(labResultFieldGroups?.field_categories ?? {}),
    [labResultFieldGroups]
  );

  // C6b: field categories for the universal Cancer Profile nested under the
  // primary cancer domain. Single-record profile table, so it mirrors Baseline's
  // category nav. Canonical lookup via cancer.skin — every cancer entry shares the
  // same universal table class (see UNIVERSAL_FIELD_GROUP_CONFIG note).
  const { fieldGroups: cancerProfileFieldGroups } = useFieldGroups(
    UNIVERSAL_FIELD_GROUP_CONFIG.cancer_profile.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.cancer_profile.tableType
  );
  // CancerProfileTable is field-scoped: its FIELD_CATEGORIES hold one "<Cancer>
  // Residue" group per cancer. Fetch the schema with THIS patient's cancer domain
  // so `hidden_scoped_fields` names the other cancers' residue columns, then drop
  // any group whose fields are all hidden — a breast patient sees "Cancer" +
  // "Breast Residue", not "Prostate Residue"/"Skin Residue"/etc.
  const { schema: cancerProfileScopedSchema } = useFieldSchema(
    domain.domainKey,
    UNIVERSAL_FIELD_GROUP_CONFIG.cancer_profile.tableType
  );
  const cancerProfileCategories = useMemo(
    () =>
      scopedCategoryNames(
        cancerProfileFieldGroups?.field_categories,
        cancerProfileScopedSchema?.hidden_scoped_fields
      ),
    [cancerProfileFieldGroups, cancerProfileScopedSchema]
  );
  const cancerProfileFilledCats = useMemo(
    () => computeFilledCategories(cancerProfileFieldGroups?.field_categories, cancerProfileData),
    [cancerProfileFieldGroups, cancerProfileData]
  );

  const { fieldGroups } = useFieldGroups(domain.domainKey, "statictable");
  const categories = useMemo(() => {
    const cats = fieldGroups?.field_categories;
    if (!cats) return [];
    return Object.keys(cats);
  }, [fieldGroups]);

  const filledCats = useMemo(
    () => computeFilledCategories(fieldGroups?.field_categories, staticData),
    [fieldGroups, staticData]
  );

  // Named field-groups nested under each History event record. Per-cancer event
  // tables group fields by event_type (FIELD_EVENT_CATEGORIES), not by named
  // FIELD_CATEGORIES, so this is normally empty and events stay flat (record
  // click focuses the row) — but a domain that declares FIELD_CATEGORIES on its
  // event table gets the nested groups automatically, same as Medications/AEs.
  const { fieldGroups: eventFieldGroups } = useFieldGroups(domain.domainKey, "eventtable");
  const eventGroupNames = useMemo(
    () => Object.keys(eventFieldGroups?.field_categories ?? {}),
    [eventFieldGroups]
  );

  const eventRecords = useMemo(
    () =>
      (domain.events ?? []).map((ev) => ({
        id: ev.event_id,
        label: `${ev.event_date ? `(${new Date(ev.event_date).getFullYear()}) ` : ""}${normalizeEventType(ev.event_type)}`,
      })),
    [domain.events]
  );

  return (
    <>
      {/* Legacy Baseline (statictable) — hidden when the retirement flag is off (066 D). */}
      {legacyTablesEnabled && (
        <>
          <NavRow
            sectionId={`${domain.domainKey}_static`}
            label={t("table_sidebar.static_data", "Baseline")}
            Icon={TableChart}
            active={staticActive}
            hasData={domain.hasStatic}
            onActivate={onSectionChange}
            indented
            dense
          />
          <Collapse in={staticActive && categories.length > 0} timeout={200} unmountOnExit>
            {categories.map((cat) => (
              <SubItem
                key={cat}
                label={cat}
                hasData={filledCats.has(cat)}
                onClick={() => onSubItemClick?.(`${domain.domainKey}_static`, cat)}
                onAdd={() => onCategoryAction?.(`${domain.domainKey}_static`, cat)}
              />
            ))}
          </Collapse>
        </>
      )}

      {/* C6b: universal Cancer Profile nested right after Baseline (before History),
          rendered once, only under the primary cancer domain. Clicking still fires
          onSectionChange("cancer_profile") → the merged DataPanel. */}
      {isPrimary && (
        <>
          <NavRow
            sectionId="cancer_profile"
            label={t("clinical.cancer_profile")}
            Icon={Fingerprint}
            active={activeSection === "cancer_profile"}
            hasData={!!hasCancerProfile}
            onActivate={onSectionChange}
            onCreateTable={
              !hasCancerProfile && onCreateCancerProfile
                ? onCreateCancerProfile
                : undefined
            }
            creating={creatingCancerProfile}
            indented
            dense
          />
          <Collapse
            in={activeSection === "cancer_profile" && cancerProfileCategories.length > 0}
            timeout={200}
            unmountOnExit
          >
            {cancerProfileCategories.map((cat) => (
              <SubItem
                key={cat}
                label={cat}
                hasData={cancerProfileFilledCats.has(cat)}
                onClick={() => onSubItemClick?.("cancer_profile", cat)}
                onAdd={() => onCategoryAction?.("cancer_profile", cat)}
              />
            ))}
          </Collapse>
        </>
      )}

      {/* Legacy History (eventtable) — hidden when the retirement flag is off (066 D). */}
      {legacyTablesEnabled && (
        <>
          <NavRow
            sectionId={`${domain.domainKey}_event`}
            label={t("table_sidebar.events", "History")}
            Icon={Timeline}
            active={eventActive}
            hasData={domain.hasEvent}
            onActivate={onSectionChange}
            indented
            dense
            addButton={onAddEvent ? (
              <SidebarAddButton
                title={t("table_sidebar.add_event", "Add event")}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddEvent(domain.domainKey, e.currentTarget as HTMLElement);
                }}
              />
            ) : undefined}
          />
          <Collapse in={eventActive} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId={`${domain.domainKey}_event`}
              records={eventRecords}
              groupNames={eventGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>
        </>
      )}

      {/* C6: universal Diagnoses/Procedures nested under the primary cancer domain,
          as sub-rows next to Baseline/History (event-style record trees like the
          domain's own History). Rendered once, only under the first cancer domain. */}
      {isPrimary && (
        <>
          <NavRow
            sectionId="diagnoses"
            label={t("data_panel.diagnoses", "Diagnoses")}
            Icon={Coronavirus}
            active={activeSection === "diagnoses"}
            hasData={!!hasDiagnoses}
            onActivate={onSectionChange}
            indented
            dense
            addButton={
              onCreateRecord ? (
                <SidebarAddButton
                  title={t("table_sidebar.add_record", "Add record")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecord("diagnoses");
                  }}
                />
              ) : undefined
            }
          />
          <Collapse in={activeSection === "diagnoses"} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId="diagnoses"
              records={diagnosisRecords ?? []}
              groupNames={diagnosisGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>

          <NavRow
            sectionId="procedures"
            label={t("data_panel.procedures", "Procedures")}
            Icon={MedicalServices}
            active={activeSection === "procedures"}
            hasData={!!hasProcedures}
            onActivate={onSectionChange}
            indented
            dense
            addButton={
              onCreateRecord ? (
                <SidebarAddButton
                  title={t("table_sidebar.add_record", "Add record")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecord("procedures");
                  }}
                />
              ) : undefined
            }
          />
          <Collapse in={activeSection === "procedures"} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId="procedures"
              records={procedureRecords ?? []}
              groupNames={procedureGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>

          <NavRow
            sectionId="tumorassessments"
            label={t("data_panel.tumor_assessment", "Staging")}
            Icon={Straighten}
            active={activeSection === "tumorassessments"}
            hasData={!!hasTumorAssessments}
            onActivate={onSectionChange}
            indented
            dense
            addButton={
              onCreateRecord ? (
                <SidebarAddButton
                  title={t("table_sidebar.add_record", "Add record")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecord("tumorassessments");
                  }}
                />
              ) : undefined
            }
          />
          <Collapse in={activeSection === "tumorassessments"} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId="tumorassessments"
              records={tumorAssessmentRecords ?? []}
              groupNames={tumorAssessmentGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>

          <NavRow
            sectionId="diseasestatuses"
            label={t("data_panel.disease_status", "Disease Status")}
            Icon={MonitorHeart}
            active={activeSection === "diseasestatuses"}
            hasData={!!hasDiseaseStatuses}
            onActivate={onSectionChange}
            indented
            dense
            addButton={
              onCreateRecord ? (
                <SidebarAddButton
                  title={t("table_sidebar.add_record", "Add record")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecord("diseasestatuses");
                  }}
                />
              ) : undefined
            }
          />
          <Collapse in={activeSection === "diseasestatuses"} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId="diseasestatuses"
              records={diseaseStatusRecords ?? []}
              groupNames={diseaseStatusGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>

          <NavRow
            sectionId="imaging"
            label={t("data_panel.imaging", "Imaging")}
            Icon={Scanner}
            active={activeSection === "imaging"}
            hasData={!!hasImaging}
            onActivate={onSectionChange}
            indented
            dense
            addButton={
              onCreateRecord ? (
                <SidebarAddButton
                  title={t("table_sidebar.add_record", "Add record")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecord("imaging");
                  }}
                />
              ) : undefined
            }
          />
          <Collapse in={activeSection === "imaging"} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId="imaging"
              records={imagingRecords ?? []}
              groupNames={imagingGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>

          <NavRow
            sectionId="systemictherapies"
            label={t("data_panel.systemic_therapy", "Systemic Therapy")}
            Icon={Vaccines}
            active={activeSection === "systemictherapies"}
            hasData={!!hasSystemicTherapies}
            onActivate={onSectionChange}
            indented
            dense
            addButton={
              onCreateRecord ? (
                <SidebarAddButton
                  title={t("table_sidebar.add_record", "Add record")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecord("systemictherapies");
                  }}
                />
              ) : undefined
            }
          />
          <Collapse in={activeSection === "systemictherapies"} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId="systemictherapies"
              records={systemicTherapyRecords ?? []}
              groupNames={systemicTherapyGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>

          <NavRow
            sectionId="labresults"
            label={t("data_panel.lab_results", "Lab Results")}
            Icon={WaterDrop}
            active={activeSection === "labresults"}
            hasData={!!hasLabResults}
            onActivate={onSectionChange}
            indented
            dense
            addButton={
              onCreateRecord ? (
                <SidebarAddButton
                  title={t("table_sidebar.add_record", "Add record")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecord("labresults");
                  }}
                />
              ) : undefined
            }
          />
          <Collapse in={activeSection === "labresults"} timeout={200} unmountOnExit>
            <EventRecordNodes
              sectionId="labresults"
              records={labResultRecords ?? []}
              groupNames={labResultGroupNames}
              onSubItemClick={onSubItemClick}
            />
          </Collapse>
        </>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------

export default function TableSidebar({
  legacyTablesEnabled = true,
  hasPatientProfile,
  hasMolecularProfile,
  hasCancerProfile,
  hasHematologicalProfile,
  hasTrialEnrollment,
  hasAdverseEvents,
  hasMedications,
  hasDiagnoses,
  hasProcedures,
  hasTumorAssessments,
  hasDiseaseStatuses,
  hasImaging,
  hasSystemicTherapies,
  hasLabResults,
  hasFamilyHistory,
  universalData,
  allDomainData,
  domainSections,
  activeSection,
  onSectionChange,
  actionButtons,
  onCreateUniversalTable,
  availableDomainTypes,
  onAddDomain,
  onAddEvent,
  onSubItemClick,
  onCategoryAction,
  onCreateRecord,
}: TableSidebarProps) {
  const { t } = useTranslation();
  const [creatingTable, setCreatingTable] = useState<string | null>(null);
  const [domainAnchor, setDomainAnchor] = useState<HTMLElement | null>(null);
  const [domainSearch, setDomainSearch] = useState("");
  const domainSearchRef = React.useRef<HTMLInputElement>(null);

  const handleCreateUniversalTable = useCallback(
    async (tableType: string, sectionId: string) => {
      if (!onCreateUniversalTable || creatingTable) return;
      setCreatingTable(tableType);
      try {
        await onCreateUniversalTable(tableType);
        onSectionChange(sectionId);
      } finally {
        setCreatingTable(null);
      }
    },
    [onCreateUniversalTable, onSectionChange, creatingTable]
  );

  const handleAddDomainClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setDomainAnchor(e.currentTarget);
    setDomainSearch("");
  }, []);

  const handleDomainClose = useCallback(() => {
    setDomainAnchor(null);
    setDomainSearch("");
  }, []);

  const handleDomainSelect = useCallback(
    (domainKey: string) => {
      onAddDomain?.(domainKey);
      handleDomainClose();
    },
    [onAddDomain, handleDomainClose]
  );

  // Joan, 2026-09-10: two of these read wrong. Molecular Profile was Biotech
  // (a flask under a microscope — "lab", not "genome"); Adverse Events was
  // Science, the SAME flask already used by Trial Enrollment two rows above,
  // so the two were indistinguishable at a glance. Diagnoses keeps
  // Coronavirus: Joan reverted that one.

  const universalItems: NavItemConfig[] = [
    {
      sectionId: "patient_profile",
      labelKey: "clinical.patient_profile",
      tableType: "patientprofiletable",
      Icon: Person,
      hasData: hasPatientProfile,
    },
    {
      sectionId: "molecular_profile",
      labelKey: "clinical.molecular_profile",
      tableType: "molecularprofiletable",
      Icon: DnaIcon,
      hasData: hasMolecularProfile,
    },
    // C6b: Cancer Profile is no longer a top-level universal item — it renders
    // nested under the primary cancer domain (see DomainExpandableNav), so a
    // clinician reads it as belonging to the cancer, not as a universal profile.
    // The underlying table stays universal (cancerprofiletable); this is display
    // placement only.
    {
      sectionId: "hematological_profile",
      labelKey: "clinical.hematological_profile",
      tableType: "hematologicalprofiletable",
      Icon: Bloodtype,
      hasData: hasHematologicalProfile,
    },
    {
      sectionId: "trial_enrollment",
      labelKey: "data_panel.trial_enrollment",
      tableType: "trialenrollmenttable",
      Icon: Science,
      hasData: hasTrialEnrollment,
    },
    {
      sectionId: "adverse_events",
      labelKey: "data_panel.adverse_events",
      tableType: "adverseeventtable",
      Icon: WarningAmber,
      hasData: hasAdverseEvents,
    },
    {
      sectionId: "medications",
      labelKey: "data_panel.medications",
      tableType: "medicationstatementtable",
      Icon: Medication,
      hasData: hasMedications,
    },
    {
      sectionId: "family_history",
      labelKey: "data_panel.family_history",
      tableType: "familyhistorytable",
      Icon: FamilyRestroom,
      hasData: hasFamilyHistory,
    },
    // C6: Diagnoses/Procedures are no longer top-level universal items — they
    // render nested under the primary cancer domain (see DomainExpandableNav).
  ];

  // Record nodes for the universal event-style tables. Labels reuse the record's
  // natural name field (medication_name / ctcae_term), with a numbered fallback.
  const medicationRecords = useMemo(
    () =>
      (universalData?.medications ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.medication_name as string) ||
            t("data_panel.medication_fallback", "Medication {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.medications, t]
  );
  const adverseEventRecords = useMemo(
    () =>
      (universalData?.adverseEvents ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.ctcae_term as string) ||
            t("data_panel.adverse_event_fallback", "Adverse Event {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.adverseEvents, t]
  );
  const diagnosisRecords = useMemo(
    () =>
      (universalData?.diagnoses ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.cancer_type as string) ||
            (row.histology_display as string) ||
            t("data_panel.diagnosis_fallback", "Diagnosis {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.diagnoses, t]
  );
  const procedureRecords = useMemo(
    () =>
      (universalData?.procedures ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.procedure_display as string) ||
            t("data_panel.procedure_fallback", "Procedure {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.procedures, t]
  );
  const tumorAssessmentRecords = useMemo(
    () =>
      (universalData?.tumorAssessments ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.stage_group as string) ||
            t("data_panel.tumor_assessment_fallback", "Staging {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.tumorAssessments, t]
  );
  const diseaseStatusRecords = useMemo(
    () =>
      (universalData?.diseaseStatuses ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.event_date as string) ||
            t("data_panel.disease_status_fallback", "Disease Status {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.diseaseStatuses, t]
  );
  const imagingRecords = useMemo(
    () =>
      (universalData?.imaging ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.modality as string) ||
            t("data_panel.imaging_fallback", "Imaging {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.imaging, t]
  );
  const systemicTherapyRecords = useMemo(
    () =>
      (universalData?.systemicTherapies ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.drug_name as string) ||
            (row.systemic_therapy as string) ||
            t("data_panel.systemic_therapy_fallback", "Systemic Therapy {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.systemicTherapies, t]
  );
  const labResultRecords = useMemo(
    () =>
      (universalData?.labResults ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.analyte as string) ||
            t("data_panel.lab_results_fallback", "Lab Result {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.labResults, t]
  );
  const familyHistoryRecords = useMemo(
    () =>
      (universalData?.familyHistory ?? [])
        .map((row, i) => ({
          id: (row.event_id ?? row.id) as string | number,
          label:
            (row.relationship as string) ||
            (row.condition as string) ||
            t("data_panel.family_history_fallback", "Family History {{n}}").replace("{{n}}", String(i + 1)),
        }))
        .filter((r) => r.id != null),
    [universalData?.familyHistory, t]
  );
  const universalEventRecords: Record<string, Array<{ id: string | number; label: string }>> = {
    medications: medicationRecords,
    adverse_events: adverseEventRecords,
    diagnoses: diagnosisRecords,
    procedures: procedureRecords,
    tumorassessments: tumorAssessmentRecords,
    diseasestatuses: diseaseStatusRecords,
    imaging: imagingRecords,
    systemictherapies: systemicTherapyRecords,
    labresults: labResultRecords,
    family_history: familyHistoryRecords,
  };

  const showDomains = domainSections.length > 0;
  const domainPopoverOpen = Boolean(domainAnchor);
  // Scope the per-cancer "add domain" list to the patient's OWN cancer(s). This
  // picker is the 43-entry deprecated per-cancer-table mechanism, so a skin
  // patient must not be offered other cancers (Breast/Gallbladder/... "Residue").
  // Universal tables (Medications, Diagnoses, Adverse Events, ...) are surfaced
  // separately above, not here. Interim fix; fully retired when the per-cancer
  // static/event tables are disabled after recall validation.
  const patientCancerKeys = React.useMemo(
    () => new Set(domainSections.map((ds) => ds.domainKey)),
    [domainSections]
  );
  const unaddedDomains =
    availableDomainTypes?.filter(
      (dt) =>
        patientCancerKeys.has(dt.value) &&
        !domainSections.some((ds) => ds.domainKey === dt.value)
    ) ?? [];

  const filteredUnaddedDomains = useMemo(() => {
    const q = domainSearch.trim().toLowerCase();
    if (!q) return unaddedDomains;
    return unaddedDomains.filter(
      (dt) =>
        dt.label.toLowerCase().includes(q) || dt.value.toLowerCase().includes(q)
    );
  }, [unaddedDomains, domainSearch]);

  // Autofocus the search input when the popover opens
  React.useEffect(() => {
    if (domainPopoverOpen) {
      const id = setTimeout(() => domainSearchRef.current?.focus(), 100);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [domainPopoverOpen]);

  return (
    <Box
      data-tour="table-sidebar"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: `linear-gradient(160deg, ${alpha(athenaColors.deepNavy, 0.95)} 0%, ${alpha(
          athenaColors.deepNavy,
          0.85
        )} 50%, ${alpha(athenaColors.royalBlue, 0.78)} 100%)`,
        boxSizing: "border-box",
      }}
    >
      {/* Inner glass panel — matches BUR card aesthetics.
*/}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          m: 1,
          py: 1.5,
          borderRadius: 2.5,
          background: alpha(athenaColors.deepNavy, 0.55),
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: `1px solid ${alpha(athenaColors.white, 0.22)}`,
        }}
      >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          ...NARROW_SCROLLBAR_SX,
        }}
      >
        <Box sx={{ px: 2, pb: 1.5, mb: 1 }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: alpha(athenaColors.white, 0.45),
            }}
          >
            {t("table_sidebar.patient_data", "Patient Data")}
          </Typography>
        </Box>

        {/* "All" top-level view — shows all sections in the content area */}
        <NavRow
          sectionId="all"
          label={t("table_sidebar.all_tables", "All Tables")}
          Icon={Dashboard}
          active={activeSection === "all"}
          hasData
          onActivate={onSectionChange}
        />

        <Box sx={{ borderBottom: `1px solid ${alpha(athenaColors.white, 0.08)}`, mx: 2, my: 0.75 }} />

        <SectionHeading>{t("table_sidebar.universal", "Universal")}</SectionHeading>
        {universalItems.map(({ sectionId, labelKey, tableType, Icon, hasData }) => {
          // Universal EVENT-style tables (Medications, Adverse Events): many rows
          // per patient → record-tree nav + "+ Add" (mirrors domain History),
          // NOT the single-record profile category nav below.
          if (
            sectionId === "medications" ||
            sectionId === "adverse_events" ||
            sectionId === "family_history"
          ) {
            return (
              <UniversalEventRecordsNav
                key={sectionId}
                sectionId={sectionId}
                label={t(labelKey)}
                Icon={Icon}
                active={activeSection === sectionId}
                hasData={hasData}
                onActivate={onSectionChange}
                onSubItemClick={onSubItemClick}
                onCreateRecord={onCreateRecord}
                records={universalEventRecords[sectionId] ?? []}
              />
            );
          }
          const dataMap: Record<string, TableRow[] | undefined> = {
            patient_profile: universalData?.patientProfile,
            molecular_profile: universalData?.molecularProfile,
            hematological_profile: universalData?.hematologicalProfile,
            trial_enrollment: universalData?.trialEnrollment,
          };
          return (
          <UniversalExpandableNav
            key={sectionId}
            sectionId={sectionId}
            label={t(labelKey)}
            Icon={Icon}
            active={activeSection === sectionId}
            hasData={hasData}
            onActivate={onSectionChange}
            onCreateTable={
              !hasData && onCreateUniversalTable
                ? () => handleCreateUniversalTable(tableType, sectionId)
                : undefined
            }
            creating={creatingTable === tableType}
            onSubItemClick={onSubItemClick}
            onCategoryAction={onCategoryAction}
            tableData={dataMap[sectionId]}
          />
          );
        })}

        {/* Divider between universal and domains */}
        {(showDomains || unaddedDomains.length > 0) && (
          <Box
            sx={{
              borderBottom: `1px solid ${alpha(athenaColors.white, 0.08)}`,
              mx: 2,
              my: 1,
            }}
          />
        )}

        <Box data-tour="table-sidebar-domain">
        {/* Domain section heading with (+) add button */}
        {(showDomains || unaddedDomains.length > 0) && (
          <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 0.5 }}>
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: "uppercase",
                color: alpha(athenaColors.white, 0.35),
                flex: 1,
              }}
            >
              {t("table_sidebar.domains", "Domains")}
            </Typography>
            {unaddedDomains.length > 0 && onAddDomain && (
              <Tooltip title={t("table_sidebar.add_domain", "Add domain")} placement="right">
                <IconButton
                  size="small"
                  onClick={handleAddDomainClick}
                  sx={{
                    p: 0,
                    width: 16,
                    height: 16,
                    color: athenaColors.periwinkle,
                    opacity: 0.6,
                    transition: "opacity 0.15s ease",
                    "&:hover": {
                      opacity: 1,
                      color: athenaColors.white,
                      backgroundColor: alpha(athenaColors.periwinkle, 0.2),
                    },
                  }}
                >
                  <Add sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {domainSections.map((domain, idx) => (
          <React.Fragment key={domain.domainKey}>
            {idx > 0 && (
              <Box
                sx={{
                  borderBottom: `1px solid ${alpha(athenaColors.white, 0.06)}`,
                  mx: 2.5,
                  my: 0.5,
                }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                pl: 2.5,
                pr: 2,
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 3,
                  height: 14,
                  borderRadius: 1,
                  backgroundColor: alpha(athenaColors.periwinkle, 0.5),
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: alpha(athenaColors.white, 0.55),
                }}
              >
                {domain.label}
              </Typography>
            </Box>
            <DomainExpandableNav
              domain={domain}
              legacyTablesEnabled={legacyTablesEnabled}
              activeSection={activeSection}
              onSectionChange={onSectionChange}
              onAddEvent={onAddEvent}
              onSubItemClick={onSubItemClick}
              onCategoryAction={onCategoryAction}
              staticData={allDomainData?.[domain.domainKey]?.static}
              isPrimary={idx === 0}
              hasCancerProfile={hasCancerProfile}
              cancerProfileData={universalData?.cancerProfile}
              onCreateCancerProfile={
                onCreateUniversalTable
                  ? () => handleCreateUniversalTable("cancerprofiletable", "cancer_profile")
                  : undefined
              }
              creatingCancerProfile={creatingTable === "cancerprofiletable"}
              hasDiagnoses={hasDiagnoses}
              hasProcedures={hasProcedures}
              hasTumorAssessments={hasTumorAssessments}
              hasDiseaseStatuses={hasDiseaseStatuses}
              hasImaging={hasImaging}
              hasSystemicTherapies={hasSystemicTherapies}
              hasLabResults={hasLabResults}
              diagnosisRecords={diagnosisRecords}
              procedureRecords={procedureRecords}
              tumorAssessmentRecords={tumorAssessmentRecords}
              diseaseStatusRecords={diseaseStatusRecords}
              imagingRecords={imagingRecords}
              systemicTherapyRecords={systemicTherapyRecords}
              labResultRecords={labResultRecords}
              onCreateRecord={onCreateRecord}
            />
          </React.Fragment>
        ))}
        </Box>{/* close data-tour="table-sidebar-domain" */}
      </Box>

      {actionButtons != null && (
        <Box
          sx={{
            flexShrink: 0,
            mt: "auto",
            pt: 2,
            px: 1.5,
          }}
        >
          {actionButtons}
        </Box>
      )}

      {/* Athena Tech branding — bottom of sidebar */}
      <Box
        sx={{
          flexShrink: 0,
          mt: "auto",
          pb: 2,
          pt: 1.5,
          display: "flex",
          justifyContent: "center",
          borderTop: `1px solid ${alpha(athenaColors.periwinkle, 0.12)}`,
          mx: 1.5,
        }}
      >
        <Box
          component="img"
          src="/brand/stacked/athena_tech/white_path.svg"
          alt="Athena Tech"
          sx={{
            width: 72,
            opacity: 0.35,
            userSelect: "none",
            pointerEvents: "none",
          }}
        />
      </Box>
      </Box>{/* close inner glass panel */}

      {/* Domain picker popover */}
      <Popover
        open={domainPopoverOpen}
        anchorEl={domainAnchor}
        onClose={handleDomainClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: SIDEBAR_WIDTH,
            maxHeight: 380,
            borderRadius: 1.5,
            backgroundColor: athenaColors.deepNavy,
            border: `1px solid ${alpha(athenaColors.periwinkle, 0.25)}`,
          },
        }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.75 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              color: alpha(athenaColors.white, 0.6),
              letterSpacing: 0.5,
              mb: 0.75,
            }}
          >
            {t("table_sidebar.select_domain", "Select domain to add")}
          </Typography>
          <TextField
            inputRef={domainSearchRef}
            size="small"
            fullWidth
            placeholder={t("add_data.domain_search_placeholder", "Search domains...")}
            value={domainSearch}
            onChange={(e) => setDomainSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: alpha(athenaColors.white, 0.5) }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.25,
                backgroundColor: alpha(athenaColors.white, 0.06),
                "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
                "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.55) },
                "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
              },
              "& .MuiOutlinedInput-input": {
                color: alpha(athenaColors.white, 0.9),
                fontSize: "0.8rem",
                py: 0.75,
                "&::placeholder": { color: alpha(athenaColors.white, 0.45), opacity: 1 },
              },
            }}
          />
        </Box>
        <List
          dense
          sx={{
            py: 0,
            maxHeight: 260,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(athenaColors.periwinkle, 0.3),
              borderRadius: 2,
            },
          }}
        >
          {filteredUnaddedDomains.length === 0 ? (
            <Typography
              sx={{
                px: 1.5,
                py: 2,
                fontSize: "0.78rem",
                textAlign: "center",
                color: alpha(athenaColors.white, 0.5),
              }}
            >
              {t("add_data.no_domain_results", "No matching domains")}
            </Typography>
          ) : (
            filteredUnaddedDomains.map((dt) => (
              <ListItemButton
                key={dt.value}
                onClick={() => handleDomainSelect(dt.value)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: alpha(athenaColors.periwinkle, 0.12),
                  },
                }}
              >
                <ListItemText
                  primary={dt.label}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { color: alpha(athenaColors.white, 0.8), fontSize: "0.82rem" },
                  }}
                />
              </ListItemButton>
            ))
          )}
        </List>
      </Popover>
    </Box>
  );
}
